'use client';

import { useState, useEffect, useCallback } from 'react';
import Icon from '@/components/ui/AppIcon';
import TourCard from './TourCard';
import FilterSidebar, { FilterState } from './FilterSidebar';

interface Tour {
  id: string;
  title: string;
  category: string;
  duration: string;
  difficulty: string;
  price: number;
  image: string;
  alt: string;
  description: string;
  highlights: string[];
  spiritualSignificance: string;
  bestSeason: string;
  groupSize: string;
  durationDays: number;
}

const mockTours: Tour[] = [
{
  id: '1',
  title: 'Complete Char Dham Yatra - Sacred Circuit',
  category: 'Char Dham Yatra',
  duration: '10 Days / 9 Nights',
  durationDays: 10,
  difficulty: 'Moderate',
  price: 85000,
  image: "https://images.unsplash.com/photo-1646970769250-4b187ea73af7",
  alt: 'Ancient Hindu temple with golden spires nestled in misty Himalayan mountains at sunrise',
  description: 'Experience the complete spiritual journey covering all four sacred dhams - Yamunotri, Gangotri, Kedarnath, and Badrinath with expert guidance and comfortable accommodations.',
  highlights: [
  'Visit all four sacred dhams in one journey',
  'Helicopter service option for Kedarnath',
  'Experienced local guides with spiritual knowledge',
  'Comfortable hotels and dharamshalas',
  'All permits and registrations included'],

  spiritualSignificance: 'Complete the sacred circuit believed to wash away sins and grant moksha',
  bestSeason: 'Summer',
  groupSize: '4-12 People'
},
{
  id: '2',
  title: 'Adi Kailash & Om Parvat - Rare Sacred Journey',
  category: 'Adi Kailash',
  duration: '14 Days / 13 Nights',
  durationDays: 14,
  difficulty: 'Challenging',
  price: 125000,
  image: "https://images.unsplash.com/photo-1675710150976-1169028a5f59",
  alt: 'Snow-covered mountain peak with natural Om symbol formation visible on rocky face against clear blue sky',
  description: 'Embark on the rare pilgrimage to Adi Kailash and witness the divine Om Parvat. This challenging journey takes you through remote Himalayan terrain to one of the most sacred yet least visited sites.',
  highlights: [
  'Witness the natural Om formation on Om Parvat',
  'Trek to Adi Kailash base camp',
  'Visit ancient Kailash Mansarovar route',
  'Experience untouched Himalayan villages',
  'All permits and border area clearances'],

  spiritualSignificance: 'Believed to be the earthly abode of Lord Shiva, offering divine blessings',
  bestSeason: 'Summer',
  groupSize: '6-10 People'
},
{
  id: '3',
  title: 'Kedarnath Dham - Premium Helicopter Package',
  category: 'Regional Circuits',
  duration: '3 Days / 2 Nights',
  durationDays: 3,
  difficulty: 'Easy',
  price: 45000,
  image: "https://images.unsplash.com/photo-1690730541046-cfdf41cc224b",
  alt: 'Ancient stone temple of Kedarnath with snow-capped peaks in background and pilgrims walking on stone pathway',
  description: 'Skip the trek and reach Kedarnath temple comfortably via helicopter. Perfect for elderly pilgrims or those with time constraints, ensuring a divine darshan without physical strain.',
  highlights: [
  'Direct helicopter service to Kedarnath',
  'VIP darshan arrangements',
  'Premium accommodation in Guptkashi',
  'Experienced pilot and ground support',
  'Weather backup plans included'],

  spiritualSignificance: 'One of the twelve Jyotirlingas, sacred abode of Lord Shiva',
  bestSeason: 'Summer',
  groupSize: '2-6 People'
},
{
  id: '4',
  title: 'Gangotri & Yamunotri - Twin Dham Journey',
  category: 'Regional Circuits',
  duration: '6 Days / 5 Nights',
  durationDays: 6,
  difficulty: 'Moderate',
  price: 38000,
  image: "https://images.unsplash.com/photo-1619187990833-6857b73ba73e",
  alt: 'Sacred Ganges river flowing through rocky gorge with temple visible on hillside surrounded by pine forests',
  description: 'Visit the sources of two holy rivers - Ganga and Yamuna. This journey combines spiritual significance with natural beauty, taking you through pristine Himalayan landscapes.',
  highlights: [
  'Darshan at Gangotri and Yamunotri temples',
  'Trek to Gaumukh glacier (optional)',
  'Visit Surya Kund hot springs',
  'Scenic drive through Uttarkashi',
  'Comfortable mountain accommodations'],

  spiritualSignificance: 'Seek blessings at the origin points of two most sacred rivers',
  bestSeason: 'Summer',
  groupSize: '4-15 People'
},
{
  id: '5',
  title: 'Badrinath & Valley of Flowers - Nature & Spirituality',
  category: 'Adventure Pilgrimages',
  duration: '8 Days / 7 Nights',
  durationDays: 8,
  difficulty: 'Moderate',
  price: 52000,
  image: "https://images.unsplash.com/photo-1707500879956-af7dc3f90c96",
  alt: 'Colorful alpine meadow filled with wildflowers in full bloom with snow-capped Himalayan peaks in background',
  description: 'Combine spiritual pilgrimage with natural wonder. Visit Badrinath temple and trek through the UNESCO World Heritage Valley of Flowers, witnessing rare Himalayan flora.',
  highlights: [
  'Badrinath temple darshan',
  'Trek through Valley of Flowers',
  'Visit Hemkund Sahib Gurudwara',
  'Witness rare Himalayan flora',
  'Expert naturalist guides'],

  spiritualSignificance: 'Badrinath is one of Char Dham, Valley of Flowers holds mythological significance',
  bestSeason: 'Summer',
  groupSize: '6-12 People'
},
{
  id: '6',
  title: 'Panch Kedar Circuit - Five Sacred Shiva Temples',
  category: 'Regional Circuits',
  duration: '12 Days / 11 Nights',
  durationDays: 12,
  difficulty: 'Challenging',
  price: 95000,
  image: "https://images.unsplash.com/photo-1591090592972-73c9f3aeb471",
  alt: 'Ancient stone temple perched on mountain cliff with prayer flags fluttering in wind and misty valleys below',
  description: 'Complete the sacred circuit of five Shiva temples - Kedarnath, Tungnath, Rudranath, Madhyamaheshwar, and Kalpeshwar. A challenging pilgrimage for devoted seekers.',
  highlights: [
  'Visit all five Panch Kedar temples',
  'Trek through remote Himalayan trails',
  'Experience ancient temple architecture',
  'Local priest blessings at each temple',
  'Camping in pristine mountain locations'],

  spiritualSignificance: 'Complete the sacred Panch Kedar circuit for ultimate spiritual merit',
  bestSeason: 'Summer',
  groupSize: '4-8 People'
},
{
  id: '7',
  title: 'Haridwar & Rishikesh - Spiritual Gateway Package',
  category: 'Family Tours',
  duration: '4 Days / 3 Nights',
  durationDays: 4,
  difficulty: 'Easy',
  price: 22000,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f91f5ae0-1763973478111.png",
  alt: 'Evening Ganga Aarti ceremony at Har Ki Pauri with hundreds of oil lamps floating on river and devotees gathered',
  description: 'Perfect introduction to Himalayan spirituality. Experience the sacred Ganga Aarti, visit ancient temples, and explore yoga capital of the world - Rishikesh.',
  highlights: [
  'Attend Ganga Aarti at Har Ki Pauri',
  'Visit Beatles Ashram and Ram Jhula',
  'River rafting in Rishikesh (optional)',
  'Temple tours with local guides',
  'Yoga and meditation sessions'],

  spiritualSignificance: 'Gateway to Char Dham, where Ganga enters the plains',
  bestSeason: 'Spring',
  groupSize: '2-20 People'
},
{
  id: '8',
  title: 'Hemkund Sahib - Sikh Pilgrimage Journey',
  category: 'Regional Circuits',
  duration: '5 Days / 4 Nights',
  durationDays: 5,
  difficulty: 'Moderate',
  price: 35000,
  image: "https://images.unsplash.com/photo-1671095724592-8d3c25e3cfc8",
  alt: 'White marble Sikh gurudwara with golden dome reflected in crystal clear glacial lake surrounded by seven peaks',
  description: 'Trek to the sacred Hemkund Sahib Gurudwara situated at 4,632 meters. A spiritually enriching journey for Sikh pilgrims and nature lovers alike.',
  highlights: [
  'Darshan at Hemkund Sahib Gurudwara',
  'Trek through scenic mountain trails',
  'Visit Lokpal Lake at high altitude',
  'Langar (community kitchen) experience',
  'Witness seven mountain peaks'],

  spiritualSignificance: 'Sacred site where Guru Gobind Singh meditated in previous life',
  bestSeason: 'Summer',
  groupSize: '4-12 People'
},
{
  id: '9',
  title: 'Nanda Devi Circuit - Adventure Pilgrimage',
  category: 'Adventure Pilgrimages',
  duration: '15 Days / 14 Nights',
  durationDays: 15,
  difficulty: 'Challenging',
  price: 135000,
  image: "https://images.unsplash.com/photo-1720882857559-b3bbbd6e4fa8",
  alt: 'Majestic snow-covered Nanda Devi peak towering above clouds with trekking trail visible in foreground',
  description: 'Circle the sacred Nanda Devi peak through remote villages and high-altitude passes. This expedition combines adventure with spiritual significance of the goddess mountain.',
  highlights: [
  'Trek around Nanda Devi sanctuary',
  'Visit remote Himalayan villages',
  'Cross high-altitude mountain passes',
  'Experience local Kumaoni culture',
  'Professional mountaineering support'],

  spiritualSignificance: 'Nanda Devi is revered as the goddess of bliss and prosperity',
  bestSeason: 'Autumn',
  groupSize: '6-10 People'
},
{
  id: '10',
  title: 'Kinnaur Kailash - Himachal Sacred Circuit',
  category: 'Regional Circuits',
  duration: '9 Days / 8 Nights',
  durationDays: 9,
  difficulty: 'Moderate',
  price: 68000,
  image: "https://images.unsplash.com/photo-1634829842509-4c7612dce391",
  alt: 'Dramatic mountain landscape with ancient Buddhist monastery perched on cliff overlooking deep valley in Himachal',
  description: 'Explore the mystical Kinnaur region and circumambulate the sacred Kinnaur Kailash. Experience the unique blend of Hindu and Buddhist cultures in remote Himachal.',
  highlights: [
  'Kinnaur Kailash parikrama',
  'Visit ancient Kalpa and Chitkul villages',
  'Experience Indo-Tibetan culture',
  'Scenic Sutlej river valley drive',
  'Local homestay experiences'],

  spiritualSignificance: 'Sacred mountain believed to be the winter abode of Lord Shiva',
  bestSeason: 'Summer',
  groupSize: '4-10 People'
},
{
  id: '11',
  title: 'Mansarovar Yatra - Ultimate Sacred Journey',
  category: 'Seasonal Specials',
  duration: '18 Days / 17 Nights',
  durationDays: 18,
  difficulty: 'Challenging',
  price: 185000,
  image: "https://images.unsplash.com/photo-1580212428350-60a9147d311b",
  alt: 'Pristine turquoise Lake Mansarovar with Mount Kailash peak visible in distance under clear blue sky',
  description: 'The ultimate pilgrimage to Mount Kailash and Lake Mansarovar in Tibet. This life-changing journey requires excellent health and strong spiritual commitment.',
  highlights: [
  'Kailash Parikrama (circumambulation)',
  'Holy dip in Lake Mansarovar',
  'Visit Tibetan monasteries',
  'Cross Dolma La Pass (5,630m)',
  'Complete medical and logistical support'],

  spiritualSignificance: 'Most sacred pilgrimage in Hinduism, Buddhism, Jainism, and Bon',
  bestSeason: 'Summer',
  groupSize: '8-15 People'
},
{
  id: '12',
  title: 'Tungnath & Chandrashila - Highest Shiva Temple Trek',
  category: 'Adventure Pilgrimages',
  duration: '4 Days / 3 Nights',
  durationDays: 4,
  difficulty: 'Moderate',
  price: 28000,
  image: "https://images.unsplash.com/photo-1554950301-ff87f67f2c3f",
  alt: 'Small ancient stone temple at high altitude with panoramic view of snow-covered Himalayan peaks at sunset',
  description: 'Trek to the world\'s highest Shiva temple at Tungnath and summit Chandrashila peak for breathtaking 360-degree Himalayan views. Perfect weekend spiritual adventure.',
  highlights: [
  'Visit world\'s highest Shiva temple',
  'Summit Chandrashila peak (4,000m)',
  'Panoramic Himalayan views',
  'Trek through rhododendron forests',
  'Camping under starlit skies'],

  spiritualSignificance: 'Third temple of Panch Kedar, where Shiva\'s arms appeared',
  bestSeason: 'Spring',
  groupSize: '4-15 People'
}];


const ToursHubInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [filteredTours, setFilteredTours] = useState<Tour[]>(mockTours);
  const [sortBy, setSortBy] = useState('recommended');
  const [activeFiltersCount, setActiveFiltersCount] = useState(0);
  const [filters, setFilters] = useState<FilterState>({
    categories: [],
    difficulty: [],
    duration: [],
    priceRange: [0, 200000],
    season: [],
    searchQuery: '',
  });

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const handleFilterChange = useCallback((newFilters: FilterState) => {
    setFilters(newFilters);
    let filtered = [...mockTours];

    // Search query
    if (newFilters.searchQuery) {
      const query = newFilters.searchQuery.toLowerCase();
      filtered = filtered.filter(
        (tour) =>
        tour.title.toLowerCase().includes(query) ||
        tour.description.toLowerCase().includes(query) ||
        tour.category.toLowerCase().includes(query)
      );
    }

    // Categories
    if (newFilters.categories.length > 0) {
      filtered = filtered.filter((tour) => newFilters.categories.includes(tour.category));
    }

    // Difficulty
    if (newFilters.difficulty.length > 0) {
      filtered = filtered.filter((tour) => newFilters.difficulty.includes(tour.difficulty));
    }

    // Duration
    if (newFilters.duration.length > 0) {
      filtered = filtered.filter((tour) => {
        const days = tour.durationDays;
        return newFilters.duration.some((range) => {
          if (range === '1-3 Days') return days >= 1 && days <= 3;
          if (range === '4-7 Days') return days >= 4 && days <= 7;
          if (range === '8-14 Days') return days >= 8 && days <= 14;
          if (range === '15+ Days') return days >= 15;
          return false;
        });
      });
    }

    // Price range
    filtered = filtered.filter(
      (tour) => tour.price >= newFilters.priceRange[0] && tour.price <= newFilters.priceRange[1]
    );

    // Season
    if (newFilters.season.length > 0) {
      filtered = filtered.filter((tour) => newFilters.season.includes(tour.bestSeason));
    }

    // Count active filters
    const count =
    newFilters.categories.length +
    newFilters.difficulty.length +
    newFilters.duration.length +
    newFilters.season.length + (
    newFilters.searchQuery ? 1 : 0) + (
    newFilters.priceRange[0] !== 0 || newFilters.priceRange[1] !== 200000 ? 1 : 0);
    setActiveFiltersCount(count);

    setFilteredTours(filtered);
  }, []);

  const handleClearFilters = useCallback(() => {
    const clearedFilters: FilterState = {
      categories: [],
      difficulty: [],
      duration: [],
      priceRange: [0, 200000],
      season: [],
      searchQuery: '',
    };
    setFilters(clearedFilters);
    setFilteredTours(mockTours);
    setActiveFiltersCount(0);
  }, []);

  const handleSortChange = (value: string) => {
    setSortBy(value);
    let sorted = [...filteredTours];

    switch (value) {
      case 'price-low':
        sorted.sort((a, b) => a.price - b.price);
        break;
      case 'price-high':
        sorted.sort((a, b) => b.price - a.price);
        break;
      case 'duration-short':
        sorted.sort((a, b) => a.durationDays - b.durationDays);
        break;
      case 'duration-long':
        sorted.sort((a, b) => b.durationDays - a.durationDays);
        break;
      case 'difficulty':
        const difficultyOrder = { Easy: 1, Moderate: 2, Challenging: 3 };
        sorted.sort(
          (a, b) =>
          difficultyOrder[a.difficulty as keyof typeof difficultyOrder] -
          difficultyOrder[b.difficulty as keyof typeof difficultyOrder]
        );
        break;
      default:
        // recommended - keep original order
        break;
    }

    setFilteredTours(sorted);
  };

  if (!isHydrated) {
    return (
      <div data-component-id="src/app/tours-hub/components/ToursHubInteractive.tsx:421:6" data-component-path="src/app/tours-hub/components/ToursHubInteractive.tsx" data-component-line="421" data-component-end-line="435" data-component-file="ToursHubInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
        <div data-component-id="src/app/tours-hub/components/ToursHubInteractive.tsx:422:8" data-component-path="src/app/tours-hub/components/ToursHubInteractive.tsx" data-component-line="422" data-component-end-line="434" data-component-file="ToursHubInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%20px-4%20sm%3Apx-6%20lg%3Apx-8%20py-8%22%7D" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div data-component-id="src/app/tours-hub/components/ToursHubInteractive.tsx:423:10" data-component-path="src/app/tours-hub/components/ToursHubInteractive.tsx" data-component-line="423" data-component-end-line="433" data-component-file="ToursHubInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22animate-pulse%20space-y-8%22%7D" className="animate-pulse space-y-8">
            <div data-component-id="src/app/tours-hub/components/ToursHubInteractive.tsx:424:12" data-component-path="src/app/tours-hub/components/ToursHubInteractive.tsx" data-component-line="424" data-component-end-line="424" data-component-file="ToursHubInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-12%20bg-muted%20rounded%20w-1%2F3%22%7D" className="h-12 bg-muted rounded w-1/3"></div>
            <div data-component-id="src/app/tours-hub/components/ToursHubInteractive.tsx:425:12" data-component-path="src/app/tours-hub/components/ToursHubInteractive.tsx" data-component-line="425" data-component-end-line="432" data-component-file="ToursHubInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20lg%3Agrid-cols-4%20gap-8%22%7D" className="grid grid-cols-1 lg:grid-cols-4 gap-8">
              <div data-component-id="src/app/tours-hub/components/ToursHubInteractive.tsx:426:14" data-component-path="src/app/tours-hub/components/ToursHubInteractive.tsx" data-component-line="426" data-component-end-line="426" data-component-file="ToursHubInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-96%20bg-muted%20rounded%22%7D" className="h-96 bg-muted rounded"></div>
              <div data-component-id="src/app/tours-hub/components/ToursHubInteractive.tsx:427:14" data-component-path="src/app/tours-hub/components/ToursHubInteractive.tsx" data-component-line="427" data-component-end-line="431" data-component-file="ToursHubInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22lg%3Acol-span-3%20grid%20grid-cols-1%20md%3Agrid-cols-2%20xl%3Agrid-cols-3%20gap-6%22%7D" className="lg:col-span-3 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div data-component-id="src/app/tours-hub/components/ToursHubInteractive.tsx:429:18" data-component-path="src/app/tours-hub/components/ToursHubInteractive.tsx" data-component-line="429" data-component-end-line="429" data-component-file="ToursHubInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-96%20bg-muted%20rounded%22%7D" key={i} className="h-96 bg-muted rounded"></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-component-id="src/app/tours-hub/components/ToursHubInteractive.tsx:440:4" data-component-path="src/app/tours-hub/components/ToursHubInteractive.tsx" data-component-line="440" data-component-end-line="573" data-component-file="ToursHubInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
      <div data-component-id="src/app/tours-hub/components/ToursHubInteractive.tsx:441:6" data-component-path="src/app/tours-hub/components/ToursHubInteractive.tsx" data-component-line="441" data-component-end-line="572" data-component-file="ToursHubInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%20px-4%20sm%3Apx-6%20lg%3Apx-8%20py-8%22%7D" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Mobile Filter Summary - Only visible on mobile */}
        <div data-component-id="src/app/tours-hub/components/ToursHubInteractive.tsx:443:8" data-component-path="src/app/tours-hub/components/ToursHubInteractive.tsx" data-component-line="443" data-component-end-line="487" data-component-file="ToursHubInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22lg%3Ahidden%20mb-6%20p-4%20bg-card%2F50%20backdrop-blur-md%20rounded-lg%20border%20border-border%22%7D" className="lg:hidden mb-6 p-4 bg-card/50 backdrop-blur-md rounded-lg border border-border">
          <div data-component-id="src/app/tours-hub/components/ToursHubInteractive.tsx:444:10" data-component-path="src/app/tours-hub/components/ToursHubInteractive.tsx" data-component-line="444" data-component-end-line="462" data-component-file="ToursHubInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%20mb-3%22%7D" className="flex items-center justify-between mb-3">
            <div data-component-id="src/app/tours-hub/components/ToursHubInteractive.tsx:445:12" data-component-path="src/app/tours-hub/components/ToursHubInteractive.tsx" data-component-line="445" data-component-end-line="453" data-component-file="ToursHubInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-2%22%7D" className="flex items-center gap-2">
              <Icon data-component-id="src/app/tours-hub/components/ToursHubInteractive.tsx:446:14" data-component-path="src/app/tours-hub/components/ToursHubInteractive.tsx" data-component-line="446" data-component-end-line="446" data-component-file="ToursHubInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22AdjustmentsHorizontalIcon%22%2C%22className%22%3A%22text-primary%22%7D" name="AdjustmentsHorizontalIcon" size={20} className="text-primary" />
              <span data-component-id="src/app/tours-hub/components/ToursHubInteractive.tsx:447:14" data-component-path="src/app/tours-hub/components/ToursHubInteractive.tsx" data-component-line="447" data-component-end-line="447" data-component-file="ToursHubInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-semibold%20text-foreground%22%2C%22textContent%22%3A%22Active%20Filters%22%7D" className="font-semibold text-foreground">Active Filters</span>
              {activeFiltersCount > 0 && (
                <span data-component-id="src/app/tours-hub/components/ToursHubInteractive.tsx:449:16" data-component-path="src/app/tours-hub/components/ToursHubInteractive.tsx" data-component-line="449" data-component-end-line="451" data-component-file="ToursHubInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22bg-primary%2F20%20text-primary%20px-2%20py-0.5%20rounded-full%20text-xs%20font-bold%22%7D" className="bg-primary/20 text-primary px-2 py-0.5 rounded-full text-xs font-bold">
                  {activeFiltersCount}
                </span>
              )}
            </div>
            {activeFiltersCount > 0 && (
              <button data-component-id="src/app/tours-hub/components/ToursHubInteractive.tsx:455:14" data-component-path="src/app/tours-hub/components/ToursHubInteractive.tsx" data-component-line="455" data-component-end-line="460" data-component-file="ToursHubInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22px-4%20py-2%20bg-white%2F10%20backdrop-blur-md%20text-white%20border%20border-white%2F20%20rounded-full%20hover%3Abg-white%2F20%20transition-all%20duration-300%20shadow-lg%20hover%3Ashadow-xl%20text-sm%20font-semibold%22%2C%22textContent%22%3A%22Clear%20All%22%7D"
                onClick={handleClearFilters}
                className="px-4 py-2 bg-white/10 backdrop-blur-md text-white border border-white/20 rounded-full hover:bg-white/20 transition-all duration-300 shadow-lg hover:shadow-xl text-sm font-semibold"
              >
                Clear All
              </button>
            )}
          </div>
          
          {/* Filter chips */}
          <div data-component-id="src/app/tours-hub/components/ToursHubInteractive.tsx:465:10" data-component-path="src/app/tours-hub/components/ToursHubInteractive.tsx" data-component-line="465" data-component-end-line="486" data-component-file="ToursHubInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-wrap%20gap-2%22%7D" className="flex flex-wrap gap-2">
            {filters.categories.map((cat) => (
              <span data-component-id="src/app/tours-hub/components/ToursHubInteractive.tsx:467:14" data-component-path="src/app/tours-hub/components/ToursHubInteractive.tsx" data-component-line="467" data-component-end-line="469" data-component-file="ToursHubInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22px-3%20py-1%20bg-primary%2F10%20text-primary%20rounded-full%20text-xs%20font-medium%22%7D" key={cat} className="px-3 py-1 bg-primary/10 text-primary rounded-full text-xs font-medium">
                {cat}
              </span>
            ))}
            {filters.difficulty.map((diff) => (
              <span data-component-id="src/app/tours-hub/components/ToursHubInteractive.tsx:472:14" data-component-path="src/app/tours-hub/components/ToursHubInteractive.tsx" data-component-line="472" data-component-end-line="474" data-component-file="ToursHubInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22px-3%20py-1%20bg-primary%2F10%20text-primary%20rounded-full%20text-xs%20font-medium%22%7D" key={diff} className="px-3 py-1 bg-primary/10 text-primary rounded-full text-xs font-medium">
                {diff}
              </span>
            ))}
            {filters.duration.map((dur) => (
              <span data-component-id="src/app/tours-hub/components/ToursHubInteractive.tsx:477:14" data-component-path="src/app/tours-hub/components/ToursHubInteractive.tsx" data-component-line="477" data-component-end-line="479" data-component-file="ToursHubInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22px-3%20py-1%20bg-primary%2F10%20text-primary%20rounded-full%20text-xs%20font-medium%22%7D" key={dur} className="px-3 py-1 bg-primary/10 text-primary rounded-full text-xs font-medium">
                {dur}
              </span>
            ))}
            {filters.season.map((sea) => (
              <span data-component-id="src/app/tours-hub/components/ToursHubInteractive.tsx:482:14" data-component-path="src/app/tours-hub/components/ToursHubInteractive.tsx" data-component-line="482" data-component-end-line="484" data-component-file="ToursHubInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22px-3%20py-1%20bg-primary%2F10%20text-primary%20rounded-full%20text-xs%20font-medium%22%7D" key={sea} className="px-3 py-1 bg-primary/10 text-primary rounded-full text-xs font-medium">
                {sea}
              </span>
            ))}
          </div>
        </div>

        {/* Main Content */}
        <div data-component-id="src/app/tours-hub/components/ToursHubInteractive.tsx:490:8" data-component-path="src/app/tours-hub/components/ToursHubInteractive.tsx" data-component-line="490" data-component-end-line="542" data-component-file="ToursHubInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20gap-8%22%7D" className="flex gap-8">
          {/* Sidebar - Hidden on mobile, visible on desktop */}
          <FilterSidebar data-component-id="src/app/tours-hub/components/ToursHubInteractive.tsx:492:10" data-component-path="src/app/tours-hub/components/ToursHubInteractive.tsx" data-component-line="492" data-component-end-line="496" data-component-file="ToursHubInteractive.tsx" data-component-name="FilterSidebar" data-component-content="%7B%22elementName%22%3A%22FilterSidebar%22%7D"
            activeFilters={filters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
          />

          {/* Tours Grid */}
          <div data-component-id="src/app/tours-hub/components/ToursHubInteractive.tsx:499:10" data-component-path="src/app/tours-hub/components/ToursHubInteractive.tsx" data-component-line="499" data-component-end-line="541" data-component-file="ToursHubInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-1%22%7D" className="flex-1">
            {/* Sort and Results */}
            <div data-component-id="src/app/tours-hub/components/ToursHubInteractive.tsx:501:12" data-component-path="src/app/tours-hub/components/ToursHubInteractive.tsx" data-component-line="501" data-component-end-line="523" data-component-file="ToursHubInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20sm%3Aflex-row%20sm%3Aitems-center%20sm%3Ajustify-between%20gap-4%20mb-6%22%7D" className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
              <div data-component-id="src/app/tours-hub/components/ToursHubInteractive.tsx:502:14" data-component-path="src/app/tours-hub/components/ToursHubInteractive.tsx" data-component-line="502" data-component-end-line="504" data-component-file="ToursHubInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-foreground%22%2C%22textContent%22%3A%22tours%20found%22%7D" className="text-foreground">
                <span data-component-id="src/app/tours-hub/components/ToursHubInteractive.tsx:503:16" data-component-path="src/app/tours-hub/components/ToursHubInteractive.tsx" data-component-line="503" data-component-end-line="503" data-component-file="ToursHubInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-semibold%22%7D" className="font-semibold">{filteredTours.length}</span> tours found
              </div>
              <div data-component-id="src/app/tours-hub/components/ToursHubInteractive.tsx:505:14" data-component-path="src/app/tours-hub/components/ToursHubInteractive.tsx" data-component-line="505" data-component-end-line="522" data-component-file="ToursHubInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-2%22%7D" className="flex items-center gap-2">
                <label data-component-id="src/app/tours-hub/components/ToursHubInteractive.tsx:506:16" data-component-path="src/app/tours-hub/components/ToursHubInteractive.tsx" data-component-line="506" data-component-end-line="508" data-component-file="ToursHubInteractive.tsx" data-component-name="label" data-component-content="%7B%22elementName%22%3A%22label%22%2C%22className%22%3A%22text-sm%20text-muted-foreground%20whitespace-nowrap%22%2C%22textContent%22%3A%22Sort%20by%3A%22%7D" htmlFor="sort" className="text-sm text-muted-foreground whitespace-nowrap">
                  Sort by:
                </label>
                <select data-component-id="src/app/tours-hub/components/ToursHubInteractive.tsx:509:16" data-component-path="src/app/tours-hub/components/ToursHubInteractive.tsx" data-component-line="509" data-component-end-line="521" data-component-file="ToursHubInteractive.tsx" data-component-name="select" data-component-content="%7B%22elementName%22%3A%22select%22%2C%22id%22%3A%22sort%22%2C%22value%22%3A%22%5Bvar%3AsortBy%5D%22%2C%22className%22%3A%22px-3%20py-2%20border%20border-input%20rounded-md%20bg-background%20text-foreground%20focus%3Aoutline-none%20focus%3Aring-2%20focus%3Aring-ring%22%7D"
                  id="sort"
                  value={sortBy}
                  onChange={(e) => handleSortChange(e.target.value)}
                  className="px-3 py-2 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option data-component-id="src/app/tours-hub/components/ToursHubInteractive.tsx:515:18" data-component-path="src/app/tours-hub/components/ToursHubInteractive.tsx" data-component-line="515" data-component-end-line="515" data-component-file="ToursHubInteractive.tsx" data-component-name="option" data-component-content="%7B%22elementName%22%3A%22option%22%2C%22value%22%3A%22recommended%22%2C%22textContent%22%3A%22Recommended%22%7D" value="recommended">Recommended</option>
                  <option data-component-id="src/app/tours-hub/components/ToursHubInteractive.tsx:516:18" data-component-path="src/app/tours-hub/components/ToursHubInteractive.tsx" data-component-line="516" data-component-end-line="516" data-component-file="ToursHubInteractive.tsx" data-component-name="option" data-component-content="%7B%22elementName%22%3A%22option%22%2C%22value%22%3A%22price-low%22%2C%22textContent%22%3A%22Price%3A%20Low%20to%20High%22%7D" value="price-low">Price: Low to High</option>
                  <option data-component-id="src/app/tours-hub/components/ToursHubInteractive.tsx:517:18" data-component-path="src/app/tours-hub/components/ToursHubInteractive.tsx" data-component-line="517" data-component-end-line="517" data-component-file="ToursHubInteractive.tsx" data-component-name="option" data-component-content="%7B%22elementName%22%3A%22option%22%2C%22value%22%3A%22price-high%22%2C%22textContent%22%3A%22Price%3A%20High%20to%20Low%22%7D" value="price-high">Price: High to Low</option>
                  <option data-component-id="src/app/tours-hub/components/ToursHubInteractive.tsx:518:18" data-component-path="src/app/tours-hub/components/ToursHubInteractive.tsx" data-component-line="518" data-component-end-line="518" data-component-file="ToursHubInteractive.tsx" data-component-name="option" data-component-content="%7B%22elementName%22%3A%22option%22%2C%22value%22%3A%22duration-short%22%2C%22textContent%22%3A%22Duration%3A%20Shortest%20First%22%7D" value="duration-short">Duration: Shortest First</option>
                  <option data-component-id="src/app/tours-hub/components/ToursHubInteractive.tsx:519:18" data-component-path="src/app/tours-hub/components/ToursHubInteractive.tsx" data-component-line="519" data-component-end-line="519" data-component-file="ToursHubInteractive.tsx" data-component-name="option" data-component-content="%7B%22elementName%22%3A%22option%22%2C%22value%22%3A%22duration-long%22%2C%22textContent%22%3A%22Duration%3A%20Longest%20First%22%7D" value="duration-long">Duration: Longest First</option>
                  <option data-component-id="src/app/tours-hub/components/ToursHubInteractive.tsx:520:18" data-component-path="src/app/tours-hub/components/ToursHubInteractive.tsx" data-component-line="520" data-component-end-line="520" data-component-file="ToursHubInteractive.tsx" data-component-name="option" data-component-content="%7B%22elementName%22%3A%22option%22%2C%22value%22%3A%22difficulty%22%2C%22textContent%22%3A%22Difficulty%20Level%22%7D" value="difficulty">Difficulty Level</option>
                </select>
              </div>
            </div>

            {/* Tours Grid */}
            {filteredTours.length > 0 ? (
              <div data-component-id="src/app/tours-hub/components/ToursHubInteractive.tsx:527:14" data-component-path="src/app/tours-hub/components/ToursHubInteractive.tsx" data-component-line="527" data-component-end-line="531" data-component-file="ToursHubInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-2%20xl%3Agrid-cols-3%20gap-6%22%7D" className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {filteredTours.map((tour) => (
                  <TourCard data-component-id="src/app/tours-hub/components/ToursHubInteractive.tsx:529:18" data-component-path="src/app/tours-hub/components/ToursHubInteractive.tsx" data-component-line="529" data-component-end-line="529" data-component-file="ToursHubInteractive.tsx" data-component-name="TourCard" data-component-content="%7B%22elementName%22%3A%22TourCard%22%7D" key={tour.id} tour={tour} />
                ))}
              </div>
            ) : (
              <div data-component-id="src/app/tours-hub/components/ToursHubInteractive.tsx:533:14" data-component-path="src/app/tours-hub/components/ToursHubInteractive.tsx" data-component-line="533" data-component-end-line="539" data-component-file="ToursHubInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20py-16%22%7D" className="text-center py-16">
                <Icon data-component-id="src/app/tours-hub/components/ToursHubInteractive.tsx:534:16" data-component-path="src/app/tours-hub/components/ToursHubInteractive.tsx" data-component-line="534" data-component-end-line="534" data-component-file="ToursHubInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22MagnifyingGlassIcon%22%2C%22className%22%3A%22mx-auto%20text-muted-foreground%20mb-4%22%7D" name="MagnifyingGlassIcon" size={64} className="mx-auto text-muted-foreground mb-4" />
                <h3 data-component-id="src/app/tours-hub/components/ToursHubInteractive.tsx:535:16" data-component-path="src/app/tours-hub/components/ToursHubInteractive.tsx" data-component-line="535" data-component-end-line="535" data-component-file="ToursHubInteractive.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22text-xl%20font-semibold%20text-foreground%20mb-2%22%2C%22textContent%22%3A%22No%20tours%20found%22%7D" className="text-xl font-semibold text-foreground mb-2">No tours found</h3>
                <p data-component-id="src/app/tours-hub/components/ToursHubInteractive.tsx:536:16" data-component-path="src/app/tours-hub/components/ToursHubInteractive.tsx" data-component-line="536" data-component-end-line="538" data-component-file="ToursHubInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-muted-foreground%20mb-6%22%2C%22textContent%22%3A%22Try%20adjusting%20your%20filters%20to%20see%20more%20results%22%7D" className="text-muted-foreground mb-6">
                  Try adjusting your filters to see more results
                </p>
              </div>
            )}
          </div>
        </div>

        {/* CTA Section */}
        <div data-component-id="src/app/tours-hub/components/ToursHubInteractive.tsx:545:8" data-component-path="src/app/tours-hub/components/ToursHubInteractive.tsx" data-component-line="545" data-component-end-line="571" data-component-file="ToursHubInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mt-16%20bg-primary%20rounded-lg%20p-8%20text-center%22%7D" className="mt-16 bg-primary rounded-lg p-8 text-center">
          <h2 data-component-id="src/app/tours-hub/components/ToursHubInteractive.tsx:546:10" data-component-path="src/app/tours-hub/components/ToursHubInteractive.tsx" data-component-line="546" data-component-end-line="548" data-component-file="ToursHubInteractive.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22font-heading%20text-2xl%20md%3Atext-3xl%20font-bold%20text-primary-foreground%20mb-4%22%2C%22textContent%22%3A%22Can't%20Find%20Your%20Perfect%20Journey%3F%22%7D" className="font-heading text-2xl md:text-3xl font-bold text-primary-foreground mb-4">
            Can't Find Your Perfect Journey?
          </h2>
          <p data-component-id="src/app/tours-hub/components/ToursHubInteractive.tsx:549:10" data-component-path="src/app/tours-hub/components/ToursHubInteractive.tsx" data-component-line="549" data-component-end-line="552" data-component-file="ToursHubInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-primary-foreground%20opacity-90%20mb-6%20max-w-2xl%20mx-auto%22%2C%22textContent%22%3A%22Our%20travel%20experts%20can%20customize%20a%20pilgrimage%20package%20tailored%20to%20your%20spiritual%20needs%2C%5Cn%20%20%20%20%20%20%20%20%20%20%20%20physical%20abilities%2C%20and%20time%20constraints.%22%7D" className="text-primary-foreground opacity-90 mb-6 max-w-2xl mx-auto">
            Our travel experts can customize a pilgrimage package tailored to your spiritual needs,
            physical abilities, and time constraints.
          </p>
          <div data-component-id="src/app/tours-hub/components/ToursHubInteractive.tsx:553:10" data-component-path="src/app/tours-hub/components/ToursHubInteractive.tsx" data-component-line="553" data-component-end-line="570" data-component-file="ToursHubInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20sm%3Aflex-row%20gap-4%20justify-center%22%7D" className="flex flex-col sm:flex-row gap-4 justify-center">
            <a data-component-id="src/app/tours-hub/components/ToursHubInteractive.tsx:554:12" data-component-path="src/app/tours-hub/components/ToursHubInteractive.tsx" data-component-line="554" data-component-end-line="562" data-component-file="ToursHubInteractive.tsx" data-component-name="a" data-component-content="%7B%22elementName%22%3A%22a%22%2C%22href%22%3A%22https%3A%2F%2Fwa.me%2F919876543210%22%2C%22className%22%3A%22px-6%20py-3%20bg-cta%20text-white%20font-semibold%20rounded-md%20hover%3Abg-opacity-90%20transition-colors%20duration-300%20flex%20items-center%20justify-center%20gap-2%22%7D"
              href="https://wa.me/919876543210"
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-3 bg-cta text-white font-semibold rounded-md hover:bg-opacity-90 transition-colors duration-300 flex items-center justify-center gap-2"
            >
              <Icon data-component-id="src/app/tours-hub/components/ToursHubInteractive.tsx:560:14" data-component-path="src/app/tours-hub/components/ToursHubInteractive.tsx" data-component-line="560" data-component-end-line="560" data-component-file="ToursHubInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ChatBubbleLeftRightIcon%22%7D" name="ChatBubbleLeftRightIcon" size={20} variant="solid" />
              <span data-component-id="src/app/tours-hub/components/ToursHubInteractive.tsx:561:14" data-component-path="src/app/tours-hub/components/ToursHubInteractive.tsx" data-component-line="561" data-component-end-line="561" data-component-file="ToursHubInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22textContent%22%3A%22WhatsApp%20Consultation%22%7D">WhatsApp Consultation</span>
            </a>
            <a data-component-id="src/app/tours-hub/components/ToursHubInteractive.tsx:563:12" data-component-path="src/app/tours-hub/components/ToursHubInteractive.tsx" data-component-line="563" data-component-end-line="569" data-component-file="ToursHubInteractive.tsx" data-component-name="a" data-component-content="%7B%22elementName%22%3A%22a%22%2C%22href%22%3A%22tel%3A%2B919876543210%22%2C%22className%22%3A%22px-6%20py-3%20bg-white%20text-primary%20font-semibold%20rounded-md%20hover%3Abg-opacity-90%20transition-colors%20duration-300%20flex%20items-center%20justify-center%20gap-2%22%7D"
              href="tel:+919876543210"
              className="px-6 py-3 bg-white text-primary font-semibold rounded-md hover:bg-opacity-90 transition-colors duration-300 flex items-center justify-center gap-2"
            >
              <Icon data-component-id="src/app/tours-hub/components/ToursHubInteractive.tsx:567:14" data-component-path="src/app/tours-hub/components/ToursHubInteractive.tsx" data-component-line="567" data-component-end-line="567" data-component-file="ToursHubInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22PhoneIcon%22%7D" name="PhoneIcon" size={20} variant="solid" />
              <span data-component-id="src/app/tours-hub/components/ToursHubInteractive.tsx:568:14" data-component-path="src/app/tours-hub/components/ToursHubInteractive.tsx" data-component-line="568" data-component-end-line="568" data-component-file="ToursHubInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22textContent%22%3A%22Call%20Now%22%7D">Call Now</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ToursHubInteractive;